import { supabase } from '@/shared/services/supabase';
import type {
  FrameReplenishmentItem,
  CreateFrameInput,
  UpdateFrameInput,
  FrameFilters,
  Manufacturer,
  MarkAsOrderedInput,
  MarkAsReceivedInput,
} from '@/shared/types/domain.types';
import type { Database } from '@/shared/types/database.types';

/**
 * Grouped manufacturer data for Weekly Ordering
 */
export interface ManufacturerGroup {
  manufacturer: Manufacturer;
  frames: FrameReplenishmentItem[];
  frameCount: number;
}

/**
 * Frames Service
 * Handles all CRUD operations for frame replenishment items
 */

export const framesService = {
  /**
   * Fetch frames with optional filters
   * RLS automatically filters by user's assigned locations
   */
  async getFrames(filters?: FrameFilters): Promise<FrameReplenishmentItem[]> {
    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        *,
        location:locations(*),
        manufacturer:manufacturers(*),
        entered_by:users!frame_replenishment_items_entered_by_user_id_fkey(*)
      `
      )
      .order('reorder_entry_date', { ascending: false });

    // Apply filters
    if (filters?.location_ids?.length) {
      query = query.in('location_id', filters.location_ids);
    }

    if (filters?.manufacturer_ids?.length) {
      query = query.in('manufacturer_id', filters.manufacturer_ids);
    }

    if (filters?.status?.length) {
      query = query.in('status', filters.status);
    }

    if (filters?.order_type) {
      query = query.eq('order_type', filters.order_type);
    }

    if (filters?.date_from) {
      query = query.gte('reorder_entry_date', filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte('reorder_entry_date', filters.date_to);
    }

    if (filters?.search) {
      query = query.or(
        `brand.ilike.%${filters.search}%,frame_name_or_model.ilike.%${filters.search}%,patient_name.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching frames:', error);
      throw new Error(error.message);
    }

    return data as FrameReplenishmentItem[];
  },

  /**
   * Get a single frame by ID
   */
  async getFrameById(id: string): Promise<FrameReplenishmentItem> {
    const { data, error } = await supabase
      .from('frame_replenishment_items')
      .select(
        `
        *,
        location:locations(*),
        manufacturer:manufacturers(*),
        entered_by:users!frame_replenishment_items_entered_by_user_id_fkey(*)
      `
      )
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching frame:', error);
      throw new Error(error.message);
    }

    return data as FrameReplenishmentItem;
  },

  /**
   * Create a new frame entry
   * Sets reorder_entry_date to today and entered_by_user_id to current user
   */
  async createFrame(input: CreateFrameInput): Promise<FrameReplenishmentItem> {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Prepare insert data
    const insertData: Database['public']['Tables']['frame_replenishment_items']['Insert'] = {
      location_id: input.location_id,
      manufacturer_id: input.manufacturer_id,
      brand: input.brand,
      frame_name_or_model: input.frame_name_or_model,
      color: input.color,
      eye_size: input.eye_size,
      order_type: input.order_type,
      patient_name: input.patient_name || null,
      tray_number: input.tray_number || null,
      notes: input.notes || null,
      entered_by_user_id: user.id,
      reorder_entry_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
      status: 'PENDING',
    };

    const { data, error } = await supabase
      .from('frame_replenishment_items')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .insert([insertData] as any)
      .select(
        `
        *,
        location:locations(*),
        manufacturer:manufacturers(*),
        entered_by:users!frame_replenishment_items_entered_by_user_id_fkey(*)
      `
      )
      .single();

    if (error) {
      console.error('Error creating frame:', error);
      throw new Error(error.message);
    }

    return data as FrameReplenishmentItem;
  },

  /**
   * Update an existing frame entry
   * Can only update frames with status = PENDING
   */
  async updateFrame(input: UpdateFrameInput): Promise<FrameReplenishmentItem> {
    const { id, ...updateData } = input;

    // Prepare update data
    const updates: Database['public']['Tables']['frame_replenishment_items']['Update'] = {
      ...(updateData.location_id && { location_id: updateData.location_id }),
      ...(updateData.manufacturer_id && { manufacturer_id: updateData.manufacturer_id }),
      ...(updateData.brand && { brand: updateData.brand }),
      ...(updateData.frame_name_or_model && { frame_name_or_model: updateData.frame_name_or_model }),
      ...(updateData.color && { color: updateData.color }),
      ...(updateData.eye_size && { eye_size: updateData.eye_size }),
      ...(updateData.order_type && { order_type: updateData.order_type }),
      patient_name: updateData.patient_name || null,
      tray_number: updateData.tray_number || null,
      notes: updateData.notes || null,
    };

    const { data, error } = await (supabase
      .from('frame_replenishment_items')
      // @ts-expect-error - Supabase type mismatch with generated types
      .update(updates)
      .eq('id', id)
      .eq('status', 'PENDING') // Only allow updating pending frames
      .select(
        `
        *,
        location:locations(*),
        manufacturer:manufacturers(*),
        entered_by:users!frame_replenishment_items_entered_by_user_id_fkey(*)
      `
      )
      .single());

    if (error) {
      console.error('Error updating frame:', error);
      throw new Error(error.message);
    }

    if (!data) {
      throw new Error('Frame not found or cannot be updated (only PENDING frames can be edited)');
    }

    return data as FrameReplenishmentItem;
  },

  /**
   * Delete a frame entry
   * Can only delete frames with status = PENDING
   */
  async deleteFrame(id: string): Promise<void> {
    const { error } = await supabase
      .from('frame_replenishment_items')
      .delete()
      .eq('id', id)
      .eq('status', 'PENDING'); // Only allow deleting pending frames

    if (error) {
      console.error('Error deleting frame:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Get pending frames grouped by manufacturer for Weekly Ordering
   * Returns array of manufacturer groups with their frames
   */
  async getFramesGroupedByManufacturer(filters?: FrameFilters): Promise<ManufacturerGroup[]> {
    // Fetch all PENDING frames with manufacturer details
    const pendingFilters: FrameFilters = {
      ...filters,
      status: ['PENDING'],
    };

    const frames = await this.getFrames(pendingFilters);

    // Group frames by manufacturer
    const groupMap = new Map<string, ManufacturerGroup>();

    frames.forEach((frame) => {
      if (!frame.manufacturer) {
        console.warn('Frame missing manufacturer data:', frame.id);
        return;
      }

      const manufacturerId = frame.manufacturer.id;

      if (!groupMap.has(manufacturerId)) {
        groupMap.set(manufacturerId, {
          manufacturer: frame.manufacturer,
          frames: [],
          frameCount: 0,
        });
      }

      const group = groupMap.get(manufacturerId)!;
      group.frames.push(frame);
      group.frameCount++;
    });

    // Convert map to array and sort by manufacturer name
    return Array.from(groupMap.values()).sort((a, b) =>
      a.manufacturer.name.localeCompare(b.manufacturer.name)
    );
  },

  /**
   * Get ordered frames grouped by manufacturer for Receiving
   * Returns array of manufacturer groups with their frames
   */
  async getOrderedFramesGroupedByManufacturer(filters?: FrameFilters): Promise<ManufacturerGroup[]> {
    // Fetch all ORDERED frames with manufacturer details
    const orderedFilters: FrameFilters = {
      ...filters,
      status: ['ORDERED'],
    };

    const frames = await this.getFrames(orderedFilters);

    // Group frames by manufacturer
    const groupMap = new Map<string, ManufacturerGroup>();

    frames.forEach((frame) => {
      if (!frame.manufacturer) {
        console.warn('Frame missing manufacturer data:', frame.id);
        return;
      }

      const manufacturerId = frame.manufacturer.id;

      if (!groupMap.has(manufacturerId)) {
        groupMap.set(manufacturerId, {
          manufacturer: frame.manufacturer,
          frames: [],
          frameCount: 0,
        });
      }

      const group = groupMap.get(manufacturerId)!;
      group.frames.push(frame);
      group.frameCount++;
    });

    // Convert map to array and sort by manufacturer name
    return Array.from(groupMap.values()).sort((a, b) =>
      a.manufacturer.name.localeCompare(b.manufacturer.name)
    );
  },

  /**
   * Mark multiple frames as ordered
   * Updates status to ORDERED and sets po_number and ordered_date
   */
  async markAsOrdered(input: MarkAsOrderedInput): Promise<FrameReplenishmentItem[]> {
    const { frame_ids, po_number, ordered_date } = input;

    if (!frame_ids || frame_ids.length === 0) {
      throw new Error('No frames selected to mark as ordered');
    }

    // Update all selected frames
    const { data, error } = await supabase
      .from('frame_replenishment_items')
      // @ts-expect-error - Supabase type mismatch with generated types
      .update({
        status: 'ORDERED',
        po_number: po_number,
        ordered_date: ordered_date,
      })
      .in('id', frame_ids)
      .eq('status', 'PENDING') // Only update frames that are currently PENDING
      .select(
        `
        *,
        location:locations(*),
        manufacturer:manufacturers(*),
        entered_by:users!frame_replenishment_items_entered_by_user_id_fkey(*)
      `
      );

    if (error) {
      console.error('Error marking frames as ordered:', error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      throw new Error('No frames were updated. They may have already been ordered or do not exist.');
    }

    return data as FrameReplenishmentItem[];
  },

  /**
   * Mark multiple frames as received
   * Updates status to RECEIVED and sets received_date
   * Can also update order_type and patient fields if needed
   */
  async markAsReceived(input: MarkAsReceivedInput): Promise<FrameReplenishmentItem[]> {
    const { frame_ids, received_date, order_type, patient_name, tray_number } = input;

    if (!frame_ids || frame_ids.length === 0) {
      throw new Error('No frames selected to mark as received');
    }

    // Prepare update data
    const updates: Database['public']['Tables']['frame_replenishment_items']['Update'] = {
      status: 'RECEIVED',
      received_date: received_date,
      order_type: order_type,
      patient_name: patient_name || null,
      tray_number: tray_number || null,
    };

    // Update all selected frames
    const { data, error } = await supabase
      .from('frame_replenishment_items')
      // @ts-expect-error - Supabase type mismatch with generated types
      .update(updates)
      .in('id', frame_ids)
      .eq('status', 'ORDERED') // Only update frames that are currently ORDERED
      .select(
        `
        *,
        location:locations(*),
        manufacturer:manufacturers(*),
        entered_by:users!frame_replenishment_items_entered_by_user_id_fkey(*)
      `
      );

    if (error) {
      console.error('Error marking frames as received:', error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      throw new Error('No frames were updated. They may have already been received or do not exist.');
    }

    return data as FrameReplenishmentItem[];
  },
};
